import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Scale, ArrowRight, AlertCircle, Home, KeyRound, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Pantalla intermedia: antes de abrir el formulario público, el
 * matriculadx de otro distrito elige su distrito de origen.
 */
export default function OtrosDistritosSelectorPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [habilitado, setHabilitado] = useState(false);
  const [distritos, setDistritos] = useState([]);
  const [anioActuacion, setAnioActuacion] = useState(null);
  const [distritoElegido, setDistritoElegido] = useState('');
  // iter192: input para pegar código de rescate directo desde el selector.
  const [codigoRescate, setCodigoRescate] = useState('');
  const [buscandoCodigo, setBuscandoCodigo] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/auxiliares-justicia/publico/estado-otros-distritos`);
        setHabilitado(Boolean(res.data.habilitado));
        setDistritos(res.data.distritos || []);
        setAnioActuacion(res.data.anio_actuacion || null);
      } catch (err) {
        console.error('Error cargando estado otros distritos:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const continuar = () => {
    if (!distritoElegido) return;
    // Guardamos distrito elegido en sessionStorage para el formulario.
    sessionStorage.setItem('aux_otro_distrito_origen', distritoElegido);
    navigate('/auxiliares-justicia/otros-distritos/formulario');
  };

  // iter192: retomar borrador con código corto desde el selector.
  const retomarConCodigo = async (e) => {
    e?.preventDefault?.();
    setErrorCodigo('');
    if (!codigoRescate.trim()) {
      setErrorCodigo('Pegá el código recibido por email.');
      return;
    }
    setBuscandoCodigo(true);
    try {
      const res = await axios.post(
        `${API_URL}/auxiliares-justicia/publico/borrador/por-codigo`,
        { codigo: codigoRescate.trim() },
      );
      const borr = res.data;
      try {
        sessionStorage.setItem('aux_otro_distrito_origen', borr.distrito_origen || '');
        localStorage.setItem(
          'aux_otro_distrito_formdata',
          JSON.stringify({
            formData: borr.datos || {},
            cursoEsteAnio: Boolean(borr.curso_este_anio),
          }),
        );
        localStorage.setItem(
          'aux_otro_distrito_borrador',
          JSON.stringify({
            token: borr.token,
            codigo: borr.codigo,
            email_contacto: borr.email_contacto,
            email_enviado: true,
            resumir_paso: 2,
          }),
        );
      } catch { /* no-op */ }
      navigate('/auxiliares-justicia/otros-distritos/formulario', { replace: true });
    } catch (err) {
      setErrorCodigo(err.response?.data?.detail || 'Código no válido.');
    } finally {
      setBuscandoCodigo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (!habilitado) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-amber-200" data-testid="otros-distritos-no-habilitado">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">Inscripción no disponible</h2>
            <p className="text-sm text-gray-600 mb-4">
              La inscripción de matriculadxs de otros distritos no está habilitada en este momento.
            </p>
            <Link to="/">
              <Button variant="outline" data-testid="btn-volver-inicio">
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-center mb-6">
          <img
            src="/logo-colegio.png"
            alt="Logo del Colegio"
            className="h-24 w-auto object-contain"
          />
        </div>

        <Card className="shadow-md" data-testid="otros-distritos-selector-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Scale className="w-6 h-6 text-amber-600" />
              <CardTitle className="text-xl">
                Inscripción · Auxiliares de Justicia
              </CardTitle>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Flujo para matriculadxs de otros distritos que desean inscribirse en los
              Departamentos Judiciales <strong>Lomas de Zamora</strong> y{' '}
              <strong>Avellaneda-Lanús</strong>.
              {anioActuacion && (
                <>
                  {' '}
                  <span className="text-xs text-gray-500 block mt-1">
                    Convocatoria para actuar en el año {anioActuacion}.
                  </span>
                </>
              )}
            </p>
          </CardHeader>

          <CardContent className="space-y-5">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Seleccioná tu distrito de origen
              </label>
              <Select value={distritoElegido} onValueChange={setDistritoElegido}>
                <SelectTrigger data-testid="selector-distrito-origen">
                  <SelectValue placeholder="— Elegí un distrito —" />
                </SelectTrigger>
                <SelectContent>
                  {distritos.map((d) => (
                    <SelectItem
                      key={d}
                      value={d}
                      data-testid={`distrito-option-${d.replace(/\s+/g, '-')}`}
                    >
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-2">
                Este dato quedará registrado en tu solicitud y es usado por el Colegio para
                ponerse en contacto con tu distrito de origen si corresponde.
              </p>
            </div>

            <Button
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              disabled={!distritoElegido}
              onClick={continuar}
              data-testid="btn-continuar-formulario"
            >
              Continuar al formulario
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="text-center">
              <Link to="/" className="text-xs text-gray-500 hover:text-gray-700">
                ← Volver al inicio
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* iter192: Card alternativa con input de código de rescate para retomar
            una inscripción ya empezada desde el mismo lugar donde arranca el flujo. */}
        <Card className="mt-6 border-blue-200 bg-blue-50/50" data-testid="selector-retomar-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-base text-blue-900">
                ¿Ya empezaste una inscripción?
              </CardTitle>
            </div>
            <p className="text-xs text-gray-700 mt-1">
              Si guardaste un borrador antes, pegá acá tu <strong>código de rescate</strong>{' '}
              (tipo <code className="bg-white px-1 rounded border">AUX-XXXXX</code>) para
              retomar donde dejaste.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={retomarConCodigo} className="space-y-3">
              <div>
                <Label htmlFor="codigo-rescate-selector" className="text-xs">
                  Código de rescate
                </Label>
                <Input
                  id="codigo-rescate-selector"
                  value={codigoRescate}
                  onChange={(e) => setCodigoRescate(e.target.value.toUpperCase())}
                  placeholder="AUX-XXXXX"
                  className="font-mono tracking-wider mt-1 bg-white"
                  data-testid="selector-input-codigo-rescate"
                />
              </div>
              {errorCodigo && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span data-testid="selector-error-codigo">{errorCodigo}</span>
                </div>
              )}
              <Button
                type="submit"
                variant="outline"
                className="w-full border-blue-300 text-blue-800 hover:bg-blue-100"
                disabled={buscandoCodigo}
                data-testid="selector-btn-retomar-codigo"
              >
                {buscandoCodigo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando…
                  </>
                ) : (
                  <>
                    Retomar inscripción
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
